import { useState, useCallback } from "react";
import {
  Table,
  Button,
  Pagination,
  Row,
  Checkbox,
  Chip,
  Snackbar,
  IconButton,
  Popover,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import Container from "../../Container";
import LimitsDialog from "../../transfer/LimitsDialog";
import getLimitsForDisplay from "../../transfer/limitsForDisplay";
import { useLimits } from "../../contexts/LimitsContext";
import { RecipientDrawer } from "../../RecipientDrawer";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import WirePaymentRow from "./WirePaymentRow";
import WireTableErrorSummary from "./WireTableErrorSummary";
import WireUnsavedRecipientDrawer from "./WireUnsavedRecipientDrawer";
import {
  useWirePaymentsTable,
  WIRE_SORT_LABEL_KEYS,
  type WireSortOption,
} from "./useWirePaymentsTable";
import useWireRecipientDrawerState from "./useWireRecipientDrawerState";
import styles from "./WirePaymentsTableScreen.module.scss";
import sortStyles from "../CreateBulk/table/BulkPaymentTable.module.scss";
import HeaderWithSteps from "../../HeaderWithSteps";

const COL_LAYOUT = {
  s: "50px 50px 300px 200px 1fr 200px",
  m: "50px 50px 300px 200px 1fr 200px",
  l: "50px 50px 300px 200px 1fr 200px",
};

const COL_LAYOUT_NO_REASON = {
  s: "50px 50px 300px 1fr 200px",
  m: "50px 50px 300px 1fr 200px",
  l: "50px 50px 300px 1fr 200px",
};

const COL_VISIBILITY: React.ComponentProps<typeof Table>["colVisibility"] = [
  "*",
  "*",
  "*",
  "*",
  "*",
  "*",
];

const COL_VISIBILITY_NO_REASON: React.ComponentProps<typeof Table>["colVisibility"] = [
  "*",
  "*",
  "*",
  "*",
  "*",
];

const WirePaymentsTableScreen = () => {
  const { l10n } = useLocalization();
  const limits = useLimits();
  const {
    hasParsedRows,
    rows,
    displayedRows,
    rowEdits,
    rowErrors,
    clickedNext,
    wireReasons,
    wireRecipients,
    currentPage,
    totalPages,
    formattedTotal,
    setCurrentPage,
    handleFieldChange,
    handleSelectRecipient,
    handleClearRecipient,
    handleAddRow,
    handleBack,
    handleNext,
    selectedRows,
    selectedTotal,
    checkboxHeaderSelected,
    toggleRowSelection,
    handleToggleCheckboxHeader,
    handleClearRowsSelection,
    handleRemoveRows,
    PAGE_SIZE,
    sortBy,
    setSortBy,
    sortPopoverOpen,
    setSortPopoverOpen,
  } = useWirePaymentsTable();

  const hasWireReasons = wireReasons.length > 0;

  const recipientDrawer = useWireRecipientDrawerState(
    wireRecipients,
    handleSelectRecipient
  );

  // Unsaved (hidden) recipient drawer state
  const [unsavedDrawerOpen, setUnsavedDrawerOpen] = useState(false);
  const [unsavedDrawerRecipient, setUnsavedDrawerRecipient] =
    useState<API.Recipient | null>(null);
  const [unsavedDrawerRowIndex, setUnsavedDrawerRowIndex] = useState<
    number | null
  >(null);

  const handleClickUnsavedRecipient = useCallback(
    (recipient: API.Recipient, rowIndex: number) => {
      setUnsavedDrawerRecipient(recipient);
      setUnsavedDrawerRowIndex(rowIndex);
      setUnsavedDrawerOpen(true);
    },
    []
  );

  const handleCloseUnsavedDrawer = useCallback(() => {
    setUnsavedDrawerOpen(false);
    setUnsavedDrawerRecipient(null);
    setUnsavedDrawerRowIndex(null);
  }, []);

  const handleUnsavedRecipientUpdate = useCallback(
    (updatedRecipient: API.Recipient) => {
      if (unsavedDrawerRowIndex !== null) {
        handleSelectRecipient(unsavedDrawerRowIndex, updatedRecipient);
      }
    },
    [unsavedDrawerRowIndex, handleSelectRecipient]
  );

  // Redirect to upload screen if context was never loaded (direct URL access)
  if (!hasParsedRows) {
    window.location.assign(`/${PAYMENT_ACTIVITY_ROUTE}/upload`);
    return null;
  }

  const limitsForDisplay = getLimitsForDisplay(limits, [
    "wire",
    "organization_user_wire",
  ]);

  return (
    <Container maxWidth="1200px">
      <HeaderWithSteps
        headerText={l10n.getString("wire-payments-table-header")}
      />
      <WireTableErrorSummary
        clickedNext={clickedNext}
        rowErrors={rowErrors}
        rowCount={rows.length}
      />
      <div className={styles.headingContainer}>
        <h4 className="fontSize--l nds-sans margin--right--s">
          {l10n.getString("wire-payments-table-subheader")}
        </h4>
        <Chip kind="primary" label={String(rows.length)} />
        <span className={sortStyles.sortDropdown}>
          <span className={sortStyles.sortButton}>
            <Popover
              content={
                <div className={sortStyles.sortMenu} role="menu">
                  {(
                    ["error", "fileOrder"] as WireSortOption[]
                  ).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={sortStyles.sortMenuItem}
                      role="menuitemradio"
                      aria-checked={sortBy === option}
                      onClick={() => {
                        setSortBy(option);
                        setSortPopoverOpen(false);
                      }}
                    >
                      <span>
                        {l10n.getString(WIRE_SORT_LABEL_KEYS[option])}
                      </span>
                      {sortBy === option && (
                        <span className="narmi-icon-check" />
                      )}
                    </button>
                  ))}
                </div>
              }
              isOpen={sortPopoverOpen}
              onUserDismiss={() => setSortPopoverOpen(false)}
            >
              <button
                type="button"
                className={sortStyles.sortTrigger}
                aria-haspopup="menu"
                aria-expanded={sortPopoverOpen}
                onClick={() => setSortPopoverOpen(!sortPopoverOpen)}
              >
                {l10n.getString("bulk-sort-by-label", {
                  sortOption: l10n.getString(
                    WIRE_SORT_LABEL_KEYS[sortBy]
                  ),
                })}
                <span className="narmi-icon-chevron-down" />
              </button>
            </Popover>
          </span>
          <span className={sortStyles.addButton}>
            <Button
              startIcon="plus"
              label={l10n.getString("label-add-payment")}
              kind="primary"
              size="s"
              onClick={handleAddRow}
            />
          </span>
        </span>
      </div>

      <Table
        kind="editable"
        colVisibility={hasWireReasons ? COL_VISIBILITY : COL_VISIBILITY_NO_REASON}
        colLayout={hasWireReasons ? COL_LAYOUT : COL_LAYOUT_NO_REASON}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>#</Table.HeaderCell>
            <Table.HeaderCell>
              <Checkbox
                onChange={handleToggleCheckboxHeader}
                checked={checkboxHeaderSelected}
              />
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("wire-payments-table-col-recipient")}
            </Table.HeaderCell>
            {hasWireReasons && (
              <Table.HeaderCell>
                {l10n.getString("wire-payments-table-col-reason")}
              </Table.HeaderCell>
            )}
            <Table.HeaderCell>
              {l10n.getString("wire-payments-table-col-memo")}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign={"right" as any}>
              {l10n.getString("wire-payments-table-col-amount")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {displayedRows.map(({ row, originalIndex }, displayIndex) => (
            <WirePaymentRow
              key={`wire-payment-row__${originalIndex}`}
              index={originalIndex}
              rowNumber={
                (currentPage - 1) * PAGE_SIZE + displayIndex + 1
              }
              recipient={row.recipient}
              recipients={wireRecipients}
              edit={rowEdits[originalIndex]}
              errors={rowErrors[originalIndex]}
              wireReasons={wireReasons}
              hasWireReasons={hasWireReasons}
              isSelected={selectedRows.includes(originalIndex)}
              onFieldChange={handleFieldChange}
              onSelectRecipient={handleSelectRecipient}
              onEditRecipient={recipientDrawer.handleClickEditRecipient}
              onAddRecipient={recipientDrawer.handleClickAddRecipient}
              onClearRecipient={handleClearRecipient}
              onClickUnsavedRecipient={handleClickUnsavedRecipient}
              onToggleSelection={toggleRowSelection}
            />
          ))}
        </Table.Body>
      </Table>

      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            totalPages={totalPages}
            selectedPage={currentPage}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
        </div>
      )}

      <div className={styles.totalRow}>
        <LimitsDialog type={["domestic_wire"]} limitsForDisplay={limitsForDisplay} />
        <div>
          <span>{l10n.getString("wire-payments-table-total-label")}</span>
          <span className="margin--left--s">{formattedTotal}</span>
        </div>
      </div>

      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("button-back")}
              onClick={handleBack}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>

      <div className={styles.stickyContainer}>
        <Snackbar isActive={Boolean(selectedRows.length)}>
          <span className="fontColor--primary fontWeight--semibold">
            {`${selectedRows.length} ${l10n.getString(
              "snackbar-payment-selected",
              { totalSelected: selectedRows.length }
            )}`}
          </span>
          <span className="fontColor--secondary">
            {l10n.getString("payment-total", {
              amount: selectedTotal,
            })}
          </span>
          <IconButton
            kind="action"
            label="Close Actions"
            name="x"
            textSize="l"
            onClick={handleClearRowsSelection}
          />
          <Snackbar.Divider />
          <Snackbar.ButtonGroup>
            <Button
              kind="primary"
              label={l10n.getString("button-delete")}
              onClick={handleRemoveRows}
            />
          </Snackbar.ButtonGroup>
        </Snackbar>
      </div>

      <RecipientDrawer
        isOpen={recipientDrawer.isOpen}
        handleClose={recipientDrawer.handleCloseDrawer}
        drawerType={recipientDrawer.drawerType}
        recipientId={recipientDrawer.selectedRecipientId}
        rowIndex={recipientDrawer.selectedRowIndex}
        fromDomesticWireFlow
      />

      <WireUnsavedRecipientDrawer
        isOpen={unsavedDrawerOpen}
        onClose={handleCloseUnsavedDrawer}
        recipient={unsavedDrawerRecipient}
        onUpdate={handleUnsavedRecipientUpdate}
      />
    </Container>
  );
};

export default WirePaymentsTableScreen;
